import React from 'react';
import { Bot, Edit, Trash2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import EmptyKnowledgeBaseState from '@/components/knowledge/EmptyKnowledgeBaseState';
import type { KnowledgeBaseDocument } from '@/components/knowledge/types';

interface DocumentsListProps {
  documents: KnowledgeBaseDocument[];
  activeDocumentId: string | null;
  onEdit: (doc: KnowledgeBaseDocument) => void;
  onDelete: (doc: KnowledgeBaseDocument) => void;
  onAddDocument: () => void;
  onProcessWithLibrarian?: (doc: KnowledgeBaseDocument) => void;
}

const DocumentsList = ({
  documents,
  activeDocumentId,
  onEdit,
  onDelete,
  onAddDocument,
  onProcessWithLibrarian,
}: DocumentsListProps) => {
  if (!documents || documents.length === 0) {
    return (
      <div className="min-h-[18rem]">
        <EmptyKnowledgeBaseState
          type="document"
          title="No source documents yet"
          description="Add raw source files first, then let Librarian turn them into vault pages."
          showButton
          onNewKnowledgeBase={onAddDocument}
          buttonText="Add source"
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-xl font-semibold">Sources</h3>
      {documents.map((doc) => (
        <Card key={doc.id} className={activeDocumentId === doc.id ? 'border-primary' : ''}>
          <CardHeader className="p-4 pb-2">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg">{doc.name}</CardTitle>
                <CardDescription>
                  Added {new Date(doc.created_at).toLocaleDateString()} • {doc.file_type}
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {onProcessWithLibrarian && (
                  <Button size="sm" variant="secondary" onClick={() => onProcessWithLibrarian(doc)}>
                    <Bot className="mr-2 h-4 w-4" />
                    Process with Librarian
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => onEdit(doc)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onDelete(doc)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            <div className="text-sm text-muted-foreground line-clamp-3">
              {doc.content.slice(0, 220)}
              {doc.content.length > 220 && '…'}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DocumentsList;
